import {
  FileSpreadsheet,
  Mail,
  MapPin,
  MessageCircle,
  MessageSquare,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Reveal } from "@/shared/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/shared/components/motion/Stagger";
import { cn } from "@/shared/lib/utils";

const CARD =
  "flex flex-col gap-5 rounded-xl glass-surface p-6 shadow-md transition-transform duration-300 ease-out hover:rotate-0";

const SOURCES: { icon: LucideIcon; label: string }[] = [
  { icon: FileSpreadsheet, label: "Spreadsheet" },
  { icon: MessageSquare, label: "Texts" },
  { icon: Mail, label: "Email" },
  { icon: MessageCircle, label: "DMs" },
];

/** Scattered source chips — contacts living in too many places at once. */
function ScatteredSourcesUi() {
  return (
    <div aria-hidden className="flex flex-wrap gap-2">
      {SOURCES.map(({ icon: Icon, label }) => (
        <span
          key={label}
          className="inline-flex items-center gap-1.5 rounded-full glass-surface-subtle px-2.5 py-1 type-label-sm text-text-muted"
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </span>
      ))}
    </div>
  );
}

/** A contact with blanks — missing address, ungrouped household. */
function MissingDetailsUi() {
  return (
    <div
      aria-hidden
      className="flex flex-col gap-2.5 rounded-lg glass-surface-subtle p-3"
    >
      <div className="flex items-center gap-2">
        <span className="h-7 w-7 rounded-full bg-brand-muted" />
        <span className="type-label-base text-text">Jordan Avery</span>
      </div>
      <div className="flex items-center gap-2">
        <MapPin className="h-3.5 w-3.5 shrink-0 text-text-muted" />
        <span className="rounded-full border border-dashed border-border-strong px-2 py-0.5 type-label-sm text-text-muted">
          Address?
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Users className="h-3.5 w-3.5 shrink-0 text-text-muted" />
        <span className="rounded-full border border-dashed border-border-strong px-2 py-0.5 type-label-sm text-text-muted">
          Household?
        </span>
      </div>
    </div>
  );
}

/** Back-and-forth chat bubbles — chasing details one reply at a time. */
function BackAndForthUi() {
  const bubble = "max-w-[85%] px-3 py-1.5 type-body-sm rounded-2xl";
  return (
    <div aria-hidden className="flex flex-col gap-2">
      <span
        className={cn(bubble, "self-start rounded-bl-sm glass-surface-subtle text-text-muted")}
      >
        What's your address?
      </span>
      <span
        className={cn(bubble, "self-end rounded-br-sm bg-brand-muted text-brand-strong")}
      >
        One sec…
      </span>
      <span
        className={cn(bubble, "self-start rounded-bl-sm glass-surface-subtle text-text-muted")}
      >
        And your plus-one?
      </span>
    </div>
  );
}

const CONCERNS = [
  {
    title: "Scattered everywhere",
    description: "Guests live across spreadsheets, group texts, emails, and DMs.",
    ui: <ScatteredSourcesUi />,
    // A deliberately askew layout — rotation + offset — to feel disorganized.
    rotation: "-rotate-2",
    offset: "md:mt-10",
  },
  {
    title: "Missing pieces",
    description: "Who's missing an address? Which names share a household?",
    ui: <MissingDetailsUi />,
    rotation: "rotate-1",
    offset: "md:mt-0",
  },
  {
    title: "Endless back-and-forth",
    description: "Chasing details from family and vendors, one reply at a time.",
    ui: <BackAndForthUi />,
    rotation: "rotate-2",
    offset: "md:mt-6",
  },
];

/**
 * Problem → solution. Each concern gets its own glass card with a small UI motif,
 * and the cards sit askew (varied rotation + vertical offset) so the cluster
 * reads as disorganized — the mess Guestbook resolves. The clean, upright
 * solution card below lands the contrast. Cards straighten on hover.
 */
export function ProblemSolutionSection() {
  return (
    <Section spacing="marketing" id="problem">
      <Container size="5xl">
        <div className="mb-16 flex flex-col items-center gap-4 text-center">
          <p className="type-label-sm tracking-wider uppercase text-text-muted">
            Why Guestbook
          </p>
          <h2 className="type-heading-md max-w-2xl text-text">
            The end of the spreadsheet shuffle.
          </h2>
        </div>

        <Stagger className="grid items-start gap-6 md:grid-cols-3">
          {CONCERNS.map((concern) => (
            <StaggerItem key={concern.title} className={concern.offset}>
              <div className={cn(CARD, concern.rotation)}>
                {concern.ui}
                <div className="flex flex-col gap-1.5">
                  <h3 className="type-heading-xs text-text">{concern.title}</h3>
                  <p className="type-body-sm text-text-muted">
                    {concern.description}
                  </p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal className="mx-auto mt-16 flex max-w-3xl flex-col items-center gap-4 text-center">
          <p className="type-label-sm tracking-wider uppercase text-brand">
            The solution
          </p>
          <p className="type-body-lg text-text">
            Artisaire Guestbook brings every contact, household, and address
            together in one organized home. Gather details once, review them at
            your own pace, and export lists in exactly the format your vendors
            need.
          </p>
        </Reveal>
      </Container>
    </Section>
  );
}
